<script lang="ts">
/**
 * @module routes/[[locale=locale]]/linkhub/+page
 * @description Link hub / "link in bio" page aggregating all social media profiles,
 * websites, and other external links. Styled as a single-column Linktree-style layout.
 */

import AuroraText from "$lib/components/AuroraText.svelte";
import ConsentIframe from "$lib/components/cookie/ConsentIframe.svelte";
import * as LinkCard from "$lib/components/link-card";
import { Share2, Globe, MoreHorizontal } from "@lucide/svelte";
import { t } from "$lib/i18n";
import { contactDetails } from "$lib/contact";

const getUrl = (id: string) =>
  contactDetails.socialMedia.find((s) => s.id === id)?.link || "";
const getIcon = (id: string) =>
  contactDetails.socialMedia.find((s) => s.id === id)?.std_icon || "";

// --- Daten-Arrays für das Linktree-Layout ---
// Jedes Objekt benötigt ein 'key' für die each-Schleife

const socialLinks = [
  {
    key: "instagram",
    url: getUrl("instagram"),
    icon: getIcon("instagram"),
  },
  {
    key: "tiktok",
    url: getUrl("tiktok"),
    icon: getIcon("tiktok"),
  },
  {
    key: "threads",
    url: getUrl("threads"),
    icon: getIcon("threads"),
  },
  {
    key: "bsky",
    url: getUrl("bsky"),
    icon: getIcon("bsky"),
  },
  {
    key: "twitter",
    url: getUrl("twitter"),
    icon: getIcon("twitter"),
  },
  {
    key: "discord",
    url: getUrl("discord"),
    icon: getIcon("discord"),
  },
  {
    key: "youtube",
    url: getUrl("youtube"),
    icon: getIcon("youtube"),
  },
];

const websiteLinks = [
  {
    key: "myWebsite",
    url: "https://mcpeapsunterstrichhd.dev",
    icon: "/pictures/logo.png",
  },
  {
    key: "cbps",
    url: "https://comboompunktsucht.app",
    icon: "/pictures/cbps_logo.png",
  },
  {
    key: "blogCBPS",
    url: "https://blog.comboompunktsucht.app",
    icon: "/pictures/cbps_logo.png",
  },
];

// Für die 'Other'-Sektion nutzen wir ein 'type'-Feld, um im Snippet das richtige Layout zu rendern
const otherLinks = [
  {
    key: "gitlab",
    type: "link",
    url: getUrl("gitlab"),
    icon: getIcon("gitlab"),
  },
  {
    key: "github",
    type: "link",
    url: getUrl("github"),
    icon: getIcon("github"),
  },
  {
    key: "playlist",
    type: "playlist",
    icon: "/pictures/linkhub/apple-musik.svg",
  },
  {
    key: "song",
    type: "song",
    icon: "/pictures/linkhub/apple-musik.svg",
  },
];

/** Stagger delay in ms between each item's animation */
const staggerDelay = 60;
</script>

<svelte:head>
  <title>{$t("sites.linkhub")} | {$t("layout.title")}</title>
</svelte:head>

<div class="linkhub-wrapper">
  <section class="text-center space-y-4">
    <AuroraText
      class="text-4xl md:text-5xl lg:text-6xl font-bold"
      colors={["#C16069", "#A2BF8A", "#C16069", "#A2BF8A"]}
      speed={3}
    >
      {$t("aboutme.name")}
    </AuroraText>
    <p class="text-muted-foreground text-lg">
      {$t("linkhub.description")}
    </p>
  </section>

  <section class="space-y-4">
    <h2
      class="text-3xl md:text-4xl font-bold text-center flex items-center justify-center gap-3"
    >
      <Share2 class="w-7 h-7 text-primary" />
      {$t("linkhub.sections.socialMedia.title")}
    </h2>
    <div class="linkhub-column">
      {#each socialLinks as link, index (link.key)}
        <div
          class="animate-item-fade"
          style:--stagger-delay="{index * staggerDelay}ms"
        >
          <LinkCard.Root
            title={$t(`linkhub.sections.socialMedia.${link.key}.title`)}
            description={$t(
              `linkhub.sections.socialMedia.${link.key}.description`,
            )}
            heading={$t(`linkhub.sections.socialMedia.${link.key}.headding`)}
            url={link.url}
            icon={link.icon}
          />
        </div>
      {/each}
    </div>
  </section>

  <section class="space-y-4">
    <h2
      class="text-3xl md:text-4xl font-bold text-center flex items-center justify-center gap-3"
    >
      <Globe class="w-7 h-7 text-primary" />
      {$t("linkhub.sections.websites.title")}
    </h2>
    <div class="linkhub-column">
      {#each websiteLinks as link, index (link.key)}
        <div
          class="animate-item-fade"
          style:--stagger-delay="{index * staggerDelay}ms"
        >
          <LinkCard.Root
            title={$t(`linkhub.sections.websites.${link.key}.title`)}
            description={$t(`linkhub.sections.websites.${link.key}.description`)}
            heading={$t(`linkhub.sections.websites.${link.key}.headding`)}
            url={link.url}
            icon={link.icon}
          />
        </div>
      {/each}
    </div>
  </section>

  <section class="space-y-4">
    <h2
      class="text-3xl md:text-4xl font-bold text-center flex items-center justify-center gap-3"
    >
      <MoreHorizontal class="w-7 h-7 text-primary" />
      {$t("linkhub.sections.other.title")}
    </h2>
    <div class="linkhub-column">
      {#each otherLinks as link, index (link.key)}
        <div
          class="animate-item-fade"
          style:--stagger-delay="{index * staggerDelay}ms"
        >
          {#if link.type === "link"}
            <LinkCard.Root
              title={$t(`linkhub.sections.other.${link.key}.title`)}
              description={$t(`linkhub.sections.other.${link.key}.description`)}
              heading={$t(`linkhub.sections.other.${link.key}.headding`)}
              url={link.url}
              icon={link.icon}
            />
          {:else if link.type === "playlist"}
            <LinkCard.Big
              title={$t(`linkhub.sections.other.${link.key}.title`)}
              heading={$t(`linkhub.sections.other.${link.key}.headding`)}
              description={$t(`linkhub.sections.other.${link.key}.description`)}
              icon={link.icon}
            >
              <ConsentIframe
                category="multimedia"
                allow="autoplay *; encrypted-media *; fullscreen *; clipboard-write"
                class="w-full rounded-lg"
                height="450"
                sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation"
                src="https://embed.music.apple.com/de/playlist/favorite/pl.u-aZb0kXDFP7zBoV2"
                title={$t("linkhub.sections.other.playlist.description")}
              />
            </LinkCard.Big>
          {:else if link.type === "song"}
            <LinkCard.Big
              title={$t(`linkhub.sections.other.${link.key}.title`)}
              heading={$t(`linkhub.sections.other.${link.key}.headding`)}
              description={$t(`linkhub.sections.other.${link.key}.description`)}
              icon={link.icon}
            >
              <ConsentIframe
                category="multimedia"
                class="w-full rounded-lg"
                height="450"
                src="https://odesli.co/embed/?url=https%3A%2F%2Fsong.link%2Ffestival_dream&theme=dark"
                allowFullScreen
                sandbox="allow-same-origin allow-scripts allow-presentation allow-popups allow-popups-to-escape-sandbox"
                allow="clipboard-read; clipboard-write"
                title={$t("linkhub.sections.other.song.description")}
              />
            </LinkCard.Big>
          {/if}
        </div>
      {/each}
    </div>
  </section>
</div>

<style>
  .linkhub-wrapper {
    display: flex;
    flex-direction: column;
    gap: 3rem;
    padding: 2.5rem 1.5rem;
    margin-inline: auto;
    width: 100%;
    max-width: 1024px;
  }

  .linkhub-column {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  @keyframes fadeSlideIn {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-item-fade {
    opacity: 0;
    animation: fadeSlideIn 0.45s ease-out forwards;
    animation-delay: var(--stagger-delay, 0ms);
  }
</style>
